import React, { useState, useMemo, useCallback, useRef, useLayoutEffect, useEffect } from 'react'
import {
    Search,
    Filter,
    ArrowUp,
    ArrowDown,
    MoveVertical,
    Columns,
    Download,
    ChevronRight,
    ChevronDown,
    ChevronLeft,
    ChevronsLeft,
    ChevronsRight,
    X,
    // GripVertical,
    SlidersHorizontal,
    ExternalLink,
    Loader2,
} from 'lucide-react'
import StatusBadge from './StatusBadge'
import Tooltip from '@/components/Tooltip'
import { formatDateTimeCell } from '@/utils/formatDateTimeCell'
import type {
    PlatformGridProps,
    GridColumn,
    GridFilter,
    GridSort,
    FilterLogic,
    ServerPagination,
} from './PlatformGrid.types'

// Renders truncated cell text and shows a project-style tooltip when overflowing
const CellText = ({ value, children }: { value: string; children: React.ReactNode }) => {
    const spanRef = React.useRef<HTMLSpanElement>(null)
    const [overflows, setOverflows] = useState(false)

    useLayoutEffect(() => {
        const el = spanRef.current
        if (!el) return
        const check = () => setOverflows(el.scrollWidth > el.offsetWidth)
        check()
        const ro = new ResizeObserver(check)
        ro.observe(el)
        return () => ro.disconnect()
    }, [value, overflows])

    const inner = (
        <span
            ref={spanRef}
            style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
            {children}
        </span>
    )

    if (overflows) {
        return (
            <Tooltip title={value} placement="top" wrapperClass="cell-tooltip-wrapper">
                {inner}
            </Tooltip>
        )
    }
    return inner
}

type Stringable = { toString(): string }

/** Reads a field off a row by its string key. */
const readField = (row: unknown, key: string): unknown =>
    (row as Record<string, unknown>)[key]

/**
 * Equivalent to `String(value ?? '')`: null/undefined become '' and every
 * other value defers to its own string conversion.
 */
function cellText(value: unknown): string {
    if (value === null || value === undefined) return ''
    const stringable: Stringable = value
    return String(stringable)
}

let entryKeyCounter = 0
const entryKeys = new WeakMap<object, string>()

/**
 * Stable React key for a filter / sort entry. Entries have no id of their own
 * (and their public shape must not change), so an identity is attached lazily
 * and carried over to updated copies with `inheritEntryKey`.
 */
const entryKey = (entry: object): string => {
    let key = entryKeys.get(entry)
    if (key === undefined) {
        entryKeyCounter += 1
        key = `entry-${entryKeyCounter}`
        entryKeys.set(entry, key)
    }
    return key
}

/** Give `next` (an updated copy of `prev`) the same key as `prev`. */
const inheritEntryKey = <E extends object>(prev: object, next: E): E => {
    entryKeys.set(next, entryKey(prev))
    return next
}

/** Group label for a row; falsy values fall into the "Unassigned" bucket. */
const groupKeyOf = (row: unknown, groupBy: string): string =>
    cellText(readField(row, groupBy) || 'Unassigned')

/**
 * Default `Array#sort` ordering (UTF-16 code units), made explicit. Only used
 * on de-duplicated values, so two arguments are never equal.
 */
const compareDistinctCodeUnits = (a: string, b: string): number =>
    a < b ? -1 : 1

/**
 * Distributes `available` px across the non-select columns proportionally to
 * their defined widths (min 55px each). The last column absorbs any rounding
 * remainder so the total never exceeds `available`.
 */
function fitColumnWidths<T>(
    nonSelectCols: GridColumn<T>[],
    available: number,
    enableSelection: boolean,
): Record<string, number> {
    const totalDefined = nonSelectCols.reduce(
        (sum, col) => sum + (col.width || 120),
        0,
    )
    const next: Record<string, number> = {}
    if (enableSelection) next['select'] = 40
    let distributed = 0
    nonSelectCols.forEach((col, index) => {
        const key = String(col.key)
        let w: number
        if (index === nonSelectCols.length - 1) {
            // Last column absorbs any rounding remainder so total never exceeds available
            w = Math.max(55, available - distributed)
        } else {
            const ratio =
                totalDefined > 0
                    ? (col.width || 120) / totalDefined
                    : 1 / nonSelectCols.length
            w = Math.max(55, Math.floor(ratio * available))
            distributed += w
        }
        next[key] = w
    })
    return next
}

function isInNumberRange(
    value: unknown,
    from: string,
    to: string | undefined,
): boolean {
    if (!from && !to) return true
    const n = Number(value)
    const upper = Number(to || from)
    const lo = Math.min(Number(from), upper)
    const hi = Math.max(Number(from), upper)
    return n >= lo && n <= hi
}

function isInDateRange(
    value: unknown,
    from: string,
    to: string | undefined,
): boolean {
    if (!from && !to) return true
    const d = new Date(String(value)).getTime()
    const d1 = new Date(from).getTime()
    const d2 = new Date(to || from).getTime()
    return d >= Math.min(d1, d2) && d <= Math.max(d1, d2)
}

function evaluateFilterOperator(value: unknown, f: GridFilter): boolean {
    const filterValue = f.value
    switch (f.operator) {
        case 'equals':
            return (
                String(value).toLowerCase() ===
                String(filterValue).toLowerCase()
            )
        case 'contains':
            return String(value)
                .toLowerCase()
                .includes(String(filterValue).toLowerCase())
        case 'startsWith':
            return String(value)
                .toLowerCase()
                .startsWith(String(filterValue).toLowerCase())
        case 'endsWith':
            return String(value)
                .toLowerCase()
                .endsWith(String(filterValue).toLowerCase())
        case 'gt':
            return Number(value) > Number(filterValue)
        case 'lt':
            return Number(value) < Number(filterValue)
        case 'gte':
            return Number(value) >= Number(filterValue)
        case 'lte':
            return Number(value) <= Number(filterValue)
        case 'between':
            return isInNumberRange(value, filterValue, f.value2)
        case 'dateRange':
            return isInDateRange(value, filterValue, f.value2)
        default:
            return true
    }
}

/** Evaluates one advanced-filter row against a data row. */
function rowMatchesFilter<T>(
    row: T,
    f: GridFilter,
    columns: GridColumn<T>[],
): boolean {
    const colDef = columns.find((c) => String(c.key) === f.field)
    const rawValue = readField(row, f.field)
    // Resolve lookup so the filter compares against what the user sees
    const value = colDef?.lookup
        ? (colDef.lookup[rawValue as string | number] ?? rawValue)
        : rawValue
    // Skip filter rows with no value entered yet
    if (!f.value && f.operator !== 'between' && f.operator !== 'dateRange')
        return true
    return evaluateFilterOperator(value, f)
}

interface PagingView {
    totalPages: number
    totalCount: number
    currentPage: number
    pageSize: number
    /** Row count the header "select all" checkbox compares against */
    selectionTotal: number
}

/** Resolves paging figures from the server pagination when given, else from local state. */
function resolvePaging(
    serverPagination: ServerPagination | undefined,
    localPage: number,
    rowsPerPage: number,
    rowCount: number,
): PagingView {
    if (serverPagination) {
        return {
            totalPages: serverPagination.totalPages,
            totalCount: serverPagination.totalCount,
            currentPage: serverPagination.page,
            pageSize: serverPagination.pageSize,
            selectionTotal: serverPagination.totalCount ?? 0,
        }
    }
    return {
        totalPages: Math.ceil(rowCount / rowsPerPage),
        totalCount: rowCount,
        currentPage: localPage,
        pageSize: rowsPerPage,
        selectionTotal: rowCount,
    }
}

/** Page-size options, including the server's current size when it isn't one of them. */
function pageSizeChoices(
    options: number[],
    current: number,
    isServerPaging: boolean,
): number[] {
    if (isServerPaging && !options.includes(current)) {
        return [...options, current].sort((a, b) => a - b)
    }
    return options
}

function pageRangeLabel(
    currentPage: number,
    pageSize: number,
    totalCount: number,
): string {
    if (totalCount === 0) return '0'
    return `${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalCount)}`
}

type PageToken = number | 'ellipsis-start' | 'ellipsis-end'

/** Page numbers to show, collapsing long ranges around the current page with ellipses. */
function buildPageList(totalPages: number, currentPage: number): PageToken[] {
    const pages: PageToken[] = []
    if (totalPages <= 7) {
        for (let i = 1; i <= totalPages; i++) pages.push(i)
        return pages
    }
    pages.push(1)
    if (currentPage > 3) pages.push('ellipsis-start')
    const last = Math.min(totalPages - 1, currentPage + 1)
    for (let i = Math.max(2, currentPage - 1); i <= last; i++) pages.push(i)
    if (currentPage < totalPages - 2) pages.push('ellipsis-end')
    pages.push(totalPages)
    return pages
}

const toolbarButtonClass = (active: boolean): string =>
    `flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium border transition-colors ${
        active
            ? 'bg-blue-50 text-blue-700 border-blue-200'
            : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'
    }`

const headerCellClass = (
    fixed: boolean | undefined,
    dragging: boolean,
    dragOver: boolean,
): string =>
    `column-header ${fixed ? 'fixed' : ''} ${dragging ? 'dragging' : ''} ${dragOver ? 'drag-over' : ''}`

const flipDirection = (
    direction: GridSort['direction'],
): GridSort['direction'] => (direction === 'asc' ? 'desc' : 'asc')

function buttonVariantClass(variant: GridColumn['buttonVariant']): string {
    if (variant === 'danger')
        return 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100'
    if (variant === 'neutral')
        return 'bg-stone-50 text-stone-700 border-stone-200 hover:bg-stone-100'
    return 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100'
}

function progressBarColor(pct: number): string {
    if (pct >= 75) return 'bg-green-500'
    if (pct >= 30) return 'bg-orange-400'
    return 'bg-red-500'
}

function downloadCsv(csv: string, filename: string): void {
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
}

const SortIcon = ({
    direction,
}: Readonly<{ direction: GridSort['direction'] | undefined }>) => {
    if (direction === undefined)
        return <MoveVertical className="w-3 h-3 text-stone-300" />
    return direction === 'asc' ? (
        <ArrowUp className="w-3 h-3 text-blue-600" />
    ) : (
        <ArrowDown className="w-3 h-3 text-blue-600" />
    )
}

/** Built-in `cellType` renderers; returns undefined when the column has none. */
function renderBuiltInCell<T>(
    col: GridColumn<T>,
    row: T,
    rawValue: unknown,
    displayValue: string,
): React.ReactNode {
    switch (col.cellType) {
        case 'icon':
            return (
                <span className="flex items-center gap-1.5">
                    {col.icon}
                    {displayValue}
                </span>
            )
        case 'button':
            return (
                <button
                    type="button"
                    className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${buttonVariantClass(col.buttonVariant)}`}
                    onClick={() => col.onButtonClick?.(row)}
                >
                    {col.buttonLabel ?? displayValue}
                </button>
            )
        case 'reference':
            return (
                <button
                    type="button"
                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline transition-colors text-xs"
                    onClick={() => col.onButtonClick?.(row)}
                >
                    {displayValue}
                    <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" />
                </button>
            )
        case 'progress': {
            const pct = Math.min(100, Math.max(0, Number(rawValue) || 0))
            return (
                <div className="flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-stone-200 rounded-full overflow-hidden">
                        <div
                            className={`h-full ${progressBarColor(pct)} rounded-full transition-all`}
                            style={{ width: `${pct}%` }}
                        />
                    </div>
                    <span className="text-xs text-stone-500 w-8 text-right">
                        {pct}%
                    </span>
                </div>
            )
        }
        default:
            return undefined
    }
}

interface CellContentArgs<T> {
    col: GridColumn<T>
    columnKey: string
    row: T
    rowIndex: number
    globalIndex: number
    isSelected: boolean
    rawValue: unknown
    displayValue: string
}

function PlatformGrid<T>({
    data,
    columns: initialColumns,
    title = 'Data Grid',
    description = 'Manage and organize your data',
    className = '',
    enableGrouping = true,
    enableFilters = true,
    enableSorting = true,
    enableColumnResize = true,
    enableColumnReorder = true,
    enableSelection = true,
    enableExport = true,
    defaultPageSize = 5,
    pageSizeOptions = [5, 10, 25, 50, 100],
    idField = 'id' as keyof T,
    loading = false,
    onRowClick,
    onSelectionChange,
    customRenderers = {},
    quickFilterColumn,
    quickFilters = [],
    serverPagination,
    onSearchChange,
    onSortChange,
    onFilterChange,
    onColumnSearchChange,
    onExport,
    onSelectAll,
    gridId = 'pg',
    fontSize = 12,
    borderRadius = 8,
    headerBg = '#f5f5f3',
    headerTextColor = '#737370',
    rowHoverBg = '#fafaf9',
    headerHoverBg = '#eeeeed',
    rowFocusBg = '#eff6ff',
    rowHeight = 32,
    cellPaddingX = 8,
}: Readonly<PlatformGridProps<T>>) {
    // Helper function to get row ID
    const getRowId = useCallback(
        (row: T): unknown => {
            return (
                row[idField] ??
                (row as Record<string, unknown>).id ??
                JSON.stringify(row)
            )
        },
        [idField],
    )

    // Add select column if selection is enabled
    const columns = useMemo(() => {
        const cols = [...initialColumns]
        if (enableSelection && !cols.some((col) => col.key === 'select')) {
            cols.unshift({
                key: 'select',
                label: '',
                fixed: true,
                width: 40,
                sortable: false,
            })
        }
        return cols
    }, [initialColumns, enableSelection])

    const [globalSearch, setGlobalSearch] = useState('')
    const [selectedRows, setSelectedRows] = useState(new Set<unknown>())
    const [statusFilter, setStatusFilter] = useState('all')
    const [activeQuickFilter, setActiveQuickFilter] = useState<number | null>(
        null,
    )
    const [sortConfig, setSortConfig] = useState<GridSort[]>([])
    const [filters, setFilters] = useState<GridFilter[]>([])
    const [groupBy, setGroupBy] = useState('')
    const [collapsedGroups, setCollapsedGroups] = useState(new Set<string>())
    const [page, setPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(defaultPageSize)
    const [isExporting, setIsExporting] = useState(false)
    const [isSelectingAll, setIsSelectingAll] = useState(false)

    const isServerPaging = !!serverPagination

    const handlePageChange = useCallback(
        (newPage: number) => {
            if (serverPagination) {
                serverPagination.onPageChange(newPage)
            } else {
                setPage(newPage)
            }
        },
        [serverPagination],
    )
    const [columnOrder, setColumnOrder] = useState(() =>
        columns.map((c) => String(c.key)),
    )
    const [visibleColumns, setVisibleColumns] = useState<
        Record<string, boolean>
    >(() => columns.reduce((acc, col) => ({ ...acc, [col.key]: col.defaultHidden !== true }), {}))

    // Sync columnOrder and visibleColumns when columns change
    React.useEffect(() => {
        const newColumnOrder = columns.map((c) => String(c.key))
        const newVisibleColumns = columns.reduce(
            (acc, col) => ({ ...acc, [col.key]: col.defaultHidden !== true }),
            {},
        )

        setColumnOrder((prev) => {
            // Preserve existing column order for columns that still exist, add new columns at the end
            const existingOrder = prev.filter((key) =>
                newColumnOrder.includes(key),
            )
            const newKeys = newColumnOrder.filter((key) => !prev.includes(key))
            return [...existingOrder, ...newKeys]
        })

        setVisibleColumns((prev) => ({
            ...prev,
            ...newVisibleColumns,
        }))
    }, [columns])
    const [showFilterPanel, setShowFilterPanel] = useState(false)
    const [showSortPanel, setShowSortPanel] = useState(false)
    const [showColumnPanel, setShowColumnPanel] = useState(false)
    const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
        () =>
            columns.reduce(
                (acc, col) => ({ ...acc, [col.key]: col.width || 120 }),
                {} as Record<string, number>,
            ),
    )

    const tableWrapRef = useRef<HTMLDivElement>(null)
    const footerRef = useRef<HTMLDivElement>(null)
    const gridRootRef = useRef<HTMLDivElement>(null)
    const hasFittedColumnsRef = useRef(false)
    const wrapChainBrokenRef = useRef(false)
    const wrapRecalcRef = useRef<() => void>(() => {})

    // Sync columnWidths when columns change
    React.useEffect(() => {
        setColumnWidths((prev) => {
            const newWidths = columns.reduce(
                (acc, col) => ({ ...acc, [col.key]: col.width || 120 }),
                {},
            )
            // Preserve custom widths for existing columns
            return { ...newWidths, ...prev }
        })
    }, [columns])

    // Fit all columns to the container width once on initial load.
    // After that, user resize changes are preserved.
    useLayoutEffect(() => {
        if (hasFittedColumnsRef.current) return
        const el = tableWrapRef.current
        if (!el) return

        const applyFit = (containerWidth: number) => {
            if (hasFittedColumnsRef.current || containerWidth <= 0) return

            const nonSelectCols = columns.filter((col) => String(col.key) !== 'select')
            // Don't lock the ref when there are no columns yet — the real columns
            // arrive after the async fetch and must still get a chance to be fitted.
            if (nonSelectCols.length === 0) return
            hasFittedColumnsRef.current = true
            const reserved = enableSelection ? 40 : 0
            const available = Math.max(0, containerWidth - reserved)

            setColumnWidths(() => fitColumnWidths(nonSelectCols, available, enableSelection))
        }

        if (el.clientWidth > 0) {
            applyFit(el.clientWidth)
            return
        }

        // Fallback: element hidden on mount (e.g. inside an inactive tab)
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting && el.clientWidth > 0) {
                applyFit(el.clientWidth)
                observer.disconnect()
            }
        }, { threshold: 0.01 })
        observer.observe(el)
        return () => observer.disconnect()
    }, [columns, enableSelection])

    // Self-sizing fallback: when the CSS flex chain from ancestors is broken
    // (e.g. a server build that predates our DTS/TabContent/LayoutRenderer fixes),
    // the root div's h-full resolves to `auto` — root grows with content, tableWrap
    // never gets a bounded height, no scrollbar, footer pushed off-screen.
    //
    // Strategy: detect once (root > available viewport space → chain is broken),
    // then permanently apply an explicit pixel height on root so flex-1 children
    // distribute space correctly. The `chainBroken` closure variable is intentionally
    // never reset — removing the fix after setting it triggers a ResizeObserver
    // re-fire loop (set → remove → root grows → re-fire → repeat).
    // When the chain IS intact (local / updated server), root.clientHeight ≤
    // available, `chainBroken` stays false, and this effect is a complete no-op.
    useEffect(() => {
        const root = gridRootRef.current
        if (!root) return

        let chainBroken = false

        const clamp = () => {
            const rootH = root.clientHeight
            if (rootH === 0) return  // not visible yet
            const top = root.getBoundingClientRect().top
            if (top < 0 || top >= window.innerHeight) return  // off-screen
            const available = Math.floor(window.innerHeight - top - 4)
            if (available <= 0) return

            if (!chainBroken && rootH > available + 10) {
                // Root clearly overflows viewport — CSS chain is broken
                chainBroken = true
            }

            if (chainBroken) {
                // Always keep the explicit height (also updates on window resize)
                root.style.height = `${available}px`
            }
            // chainBroken=false → CSS h-full is in effect → leave root untouched
        }

        const ro = new ResizeObserver(clamp)
        ro.observe(root)
        window.addEventListener('resize', clamp, { passive: true })
        return () => {
            ro.disconnect()
            window.removeEventListener('resize', clamp)
        }
    }, [])

    // Direct viewport-clamp on the scroll wrapper — handles both directions:
    //   grow (5→25 rows): clamps wrapper to available viewport height → scrollbar
    //   shrink (25→5 rows): releases clamp so wrapper contracts with content
    //
    // Why observe the inner <table> as well as the wrapper:
    // When we apply an explicit height to the wrapper, the wrapper's own clientHeight
    // no longer changes when rows are added/removed — only the inner table changes.
    // By observing the table we get a ResizeObserver callback on every row-count change,
    // then `scrollHeight > avail` re-evaluates dynamically each time.
    //
    // No persistent flag: scrollHeight is always compared fresh against the current
    // available space — the clamp auto-releases when content fits and auto-applies
    // when it overflows, regardless of prior direction.
    useEffect(() => {
        const wrap = tableWrapRef.current
        const footer = footerRef.current
        if (!wrap || !footer) return

        const recalc = () => {
            const rect = wrap.getBoundingClientRect()
            if (rect.top <= 0 || rect.top >= window.innerHeight) return
            const footerH = footer.getBoundingClientRect().height
            // Never floor this at "6 rows worth" — when other chrome above the
            // wrapper grows (e.g. the selection banner appears after a row is
            // checked), the true remaining space can legitimately be less than
            // 6 rows. Forcing a taller-than-available height here is what pushes
            // the footer (rendered after wrap in DOM order) outside the card's
            // overflow-hidden bounds, hiding it entirely.
            const avail = Math.max(
                0,
                Math.floor(window.innerHeight - rect.top - footerH - 4),
            )
            // Detect broken CSS height chain: wrap physically extends past viewport
            if (!wrapChainBrokenRef.current && rect.bottom > window.innerHeight + 10) {
                wrapChainBrokenRef.current = true
            }
            if (wrapChainBrokenRef.current) {
                if (wrap.scrollHeight > avail + 10) {
                    wrap.style.flex = 'none'
                    wrap.style.height = `${avail}px`
                } else {
                    wrap.style.removeProperty('height')
                    wrap.style.removeProperty('flex')
                }
            }
        }

        wrapRecalcRef.current = recalc

        const ro = new ResizeObserver(recalc)
        ro.observe(wrap)
        window.addEventListener('resize', recalc, { passive: true })
        return () => {
            ro.disconnect()
            window.removeEventListener('resize', recalc)
        }
    }, [rowHeight])

    // Re-run recalc after every data change (rows shrink/grow on a page-size
    // switch) and after every selection change (the selection banner above the
    // wrapper appears/disappears, changing how much space is actually left).
    // useLayoutEffect fires synchronously after DOM mutations, so measurements
    // reflect the new layout before the browser paints — without this, the fix
    // would rely solely on the ResizeObserver's async callback, leaving a brief
    // window where the footer is clipped before it self-corrects.
    useLayoutEffect(() => {
        wrapRecalcRef.current()
    }, [data, selectedRows])

    const [filterLogic, setFilterLogic] = useState<FilterLogic>('AND')
    const [draggedColumn, setDraggedColumn] = useState<string | null>(null)
    const [dragOverColumn, setDragOverColumn] = useState<string | null>(null)
    const [resizingColumn, setResizingColumn] = useState<string | null>(null)
    const [showColumnSearch, setShowColumnSearch] = useState(false)
    const [columnSearchValues, setColumnSearchValues] = useState<
        Record<string, string>
    >({})
    const [goToPage, setGoToPage] = useState('')

    // Notify parent of sort/filter/column-search changes for server-side handling.
    // Each ref skips the initial render so mounting never triggers a spurious refetch.
    const sortNotifiedRef = useRef(false)
    const filterNotifiedRef = useRef(false)
    const colSearchNotifiedRef = useRef(false)

    useEffect(() => {
        if (!sortNotifiedRef.current) { sortNotifiedRef.current = true; return }
        onSortChange?.(sortConfig)
    }, [sortConfig, onSortChange])

    useEffect(() => {
        if (!filterNotifiedRef.current) { filterNotifiedRef.current = true; return }
        onFilterChange?.(filters)
    }, [filters, onFilterChange])

    useEffect(() => {
        if (!colSearchNotifiedRef.current) { colSearchNotifiedRef.current = true; return }
        onColumnSearchChange?.(columnSearchValues)
    }, [columnSearchValues, onColumnSearchChange])

    const orderedColumns = useMemo(() => {
        return columnOrder
            .map((key) => columns.find((col) => String(col.key) === key))
            .filter(
                (col): col is NonNullable<typeof col> =>
                    !!col && !!visibleColumns[String(col.key)],
            )
    }, [columnOrder, visibleColumns, columns])

    const totalColumnWidth = useMemo(
        () => orderedColumns.reduce((sum, col) => sum + (columnWidths[String(col.key)] || 0), 0),
        [orderedColumns, columnWidths],
    )

    const quickFilterPills = useMemo(() => {
        if (!quickFilterColumn) return []
        const key = String(quickFilterColumn)
        return [...new Set(data.map((r) => cellText(readField(r, key))))]
            .filter(Boolean)
            .sort(compareDistinctCodeUnits)
    }, [data, quickFilterColumn])

    // Keys eligible for global search (searchable !== false)
    const searchableKeys = useMemo(
        () =>
            initialColumns
                .filter((col) => col.searchable !== false)
                .map((col) => String(col.key)),
        [initialColumns],
    )

    const filteredData = useMemo(() => {
        let result = [...data]
        if (globalSearch && !onSearchChange) {
            const query = globalSearch.toLowerCase()
            result = result.filter((row) =>
                searchableKeys.some((key) =>
                    cellText(readField(row, key)).toLowerCase().includes(query),
                ),
            )
        }
        if (statusFilter !== 'all' && quickFilterColumn) {
            const key = String(quickFilterColumn)
            result = result.filter(
                (row) =>
                    String((row as Record<string, unknown>)[key]) ===
                    statusFilter,
            )
        }
        if (filters.length > 0) {
            result = result.filter((row) => {
                const results = filters.map((f) =>
                    rowMatchesFilter(row, f, initialColumns),
                )
                return filterLogic === 'AND'
                    ? results.every(Boolean)
                    : results.some(Boolean)
            })
        }
        // Quick filter (single active)
        if (activeQuickFilter !== null && quickFilters[activeQuickFilter]) {
            result = result.filter((row) =>
                quickFilters[activeQuickFilter].filter(row),
            )
        }
        // Per-column search
        Object.entries(columnSearchValues).forEach(([key, val]) => {
            if (!val) return
            const q = val.toLowerCase()
            const colDef = initialColumns.find((c) => String(c.key) === key)
            result = result.filter((row) => {
                const rawValue = (row as Record<string, unknown>)[key]
                const displayValue = colDef?.lookup
                    ? (colDef.lookup[rawValue as string | number] ??
                      formatDateTimeCell(rawValue))
                    : formatDateTimeCell(rawValue)
                return displayValue.toLowerCase().includes(q)
            })
        })
        return result
    }, [
        data,
        globalSearch,
        onSearchChange,
        searchableKeys,
        statusFilter,
        quickFilterColumn,
        filters,
        filterLogic,
        columnSearchValues,
        activeQuickFilter,
        quickFilters,
        initialColumns,
    ])

    const sortedData = useMemo(() => {
        if (sortConfig.length === 0) return filteredData
        return [...filteredData].sort((a, b) => {
            for (const { field, direction } of sortConfig) {
                const aVal = (a as Record<string, unknown>)[field],
                    bVal = (b as Record<string, unknown>)[field]
                const comparison =
                    typeof aVal === 'number' && typeof bVal === 'number'
                        ? aVal - bVal
                        : String(aVal).localeCompare(String(bVal))
                if (comparison !== 0)
                    return direction === 'asc' ? comparison : -comparison
            }
            return 0
        })
    }, [filteredData, sortConfig])

    // Auto-select the first row on initial load so it shows as highlighted.
    // Gated on `!loading`: a grid that remounts on a mutation-triggered refresh
    // (e.g. delete) renders this fresh instance with the PARENT's still-stale
    // pre-refetch `data` for one commit before the new fetch resolves — the
    // parent flips `loading` true first and only swaps `data` once the response
    // lands. Firing on that first (stale) commit would permanently lock this
    // one-shot ref onto a row that no longer represents the refreshed list.
    const firstRowAutoSelectedRef = useRef(false)
    useEffect(() => {
        if (
            !firstRowAutoSelectedRef.current &&
            !loading &&
            enableSelection &&
            sortedData.length > 0
        ) {
            firstRowAutoSelectedRef.current = true
            const firstRow = sortedData[0]
            const firstId = getRowId(firstRow)
            selectedRowsMapRef.current.set(firstId, firstRow)
            setSelectedRows(prev => {
                if (prev.has(firstId)) return prev
                const next = new Set(prev)
                next.add(firstId)
                return next
            })
        }
    }, [sortedData, getRowId, enableSelection, loading])

    // Drive the header checkbox indeterminate attribute (some-but-not-all selected).
    // Must compare against the server-side total (not the current page's row count)
    // in server-paging mode, matching the `checked` calc below — otherwise a
    // cross-page selection reads as neither checked nor indeterminate.
    const headerCheckboxRef = useRef<HTMLInputElement>(null)
    const { totalPages, totalCount, currentPage, pageSize, selectionTotal } =
        resolvePaging(serverPagination, page, rowsPerPage, sortedData.length)
    useEffect(() => {
        if (headerCheckboxRef.current) {
            headerCheckboxRef.current.indeterminate =
                selectedRows.size > 0 && selectedRows.size < selectionTotal
        }
    }, [selectedRows.size, selectionTotal])

    // totalGroupCounts: full group sizes across all pages, used for group header labels
    const totalGroupCounts = useMemo(() => {
        const counts: Record<string, number> = {}
        if (!groupBy) return counts
        sortedData.forEach((row) => {
            const key = groupKeyOf(row, groupBy)
            counts[key] = (counts[key] ?? 0) + 1
        })
        return counts
    }, [sortedData, groupBy])

    const paginatedData = useMemo(() => {
        const rows = isServerPaging
            ? sortedData
            : sortedData.slice((page - 1) * rowsPerPage, page * rowsPerPage)
        if (!groupBy) return { '': rows }
        const groups: Record<string, T[]> = {}
        rows.forEach((row) => {
            const key = groupKeyOf(row, groupBy)
            if (!groups[key]) groups[key] = []
            groups[key].push(row)
        })
        return groups
    }, [sortedData, page, rowsPerPage, groupBy, isServerPaging])

    const handleResizeStart = useCallback(
        (e: React.MouseEvent, columnKey: string) => {
            e.preventDefault()
            e.stopPropagation()

            const startX = e.clientX
            const startWidth = columnWidths[columnKey]

            setResizingColumn(columnKey)

            const onMouseMove = (mv: MouseEvent) => {
                const newWidth = Math.max(
                    55,
                    startWidth + (mv.clientX - startX),
                )
                setColumnWidths((prev) => ({ ...prev, [columnKey]: newWidth }))
            }

            const onMouseUp = () => {
                setResizingColumn(null)
                document.removeEventListener('mousemove', onMouseMove)
                document.removeEventListener('mouseup', onMouseUp)
            }

            document.addEventListener('mousemove', onMouseMove)
            document.addEventListener('mouseup', onMouseUp)
        },
        [columnWidths],
    )

    const handleDragStart = useCallback(
        (e: React.DragEvent<HTMLElement>, columnKey: string) => {
            if (columns.find((c) => String(c.key) === columnKey)?.fixed) {
                e.preventDefault()
                return
            }
            setDraggedColumn(columnKey)
            e.dataTransfer.effectAllowed = 'move'
            setTimeout(() => {
                ;(e.target as HTMLElement).style.opacity = '0.4'
            }, 0)
        },
        [columns],
    )

    const handleDragEnd = useCallback((e: React.DragEvent<HTMLElement>) => {
        ;(e.target as HTMLElement).style.opacity = ''
        setDraggedColumn(null)
        setDragOverColumn(null)
    }, [])

    const handleDragOver = useCallback(
        (e: React.DragEvent<HTMLElement>, columnKey: string) => {
            e.preventDefault()
            const column = columns.find((c) => String(c.key) === columnKey)
            if (draggedColumn && draggedColumn !== columnKey && !column?.fixed)
                setDragOverColumn(columnKey)
        },
        [draggedColumn, columns],
    )

    const handleDrop = useCallback(
        (e: React.DragEvent<HTMLElement>, targetColumnKey: string) => {
            e.preventDefault()
            if (!draggedColumn || draggedColumn === targetColumnKey) {
                setDragOverColumn(null)
                return
            }
            if (columns.find((c) => String(c.key) === targetColumnKey)?.fixed) {
                setDragOverColumn(null)
                return
            }
            setColumnOrder((prev) => {
                const newOrder = [...prev]
                const from = newOrder.indexOf(draggedColumn)
                const to = newOrder.indexOf(targetColumnKey)
                newOrder.splice(from, 1)
                newOrder.splice(to, 0, draggedColumn)
                return newOrder
            })
            setDragOverColumn(null)
        },
        [draggedColumn, columns],
    )

    const lastSelectedIndexRef = useRef<number | null>(null)
    // Stores full row objects keyed by row ID so "Export Selected" always has the
    // data even after a page change or a "Select All" that fetched records not
    // currently in sortedData.
    const selectedRowsMapRef = useRef(new Map<unknown, T>())
    // Tracks whether the current selection was produced by the header "select
    // all" click, independent of `selectedRows.size`. The first row is
    // auto-selected on load (see the effect above), so `size > 0` is true
    // before the user ever touches the header checkbox — comparing against
    // that would deselect instead of selecting all on the first click.
    // Needed on top of the size/selectionTotal check below because grids with
    // a `clientFilter` (e.g. the /users/grid lists, which drop soft-deleted
    // rows after fetch) report a `totalCount` captured before that filter
    // runs, so the filtered `selectedRows.size` could never equal it and
    // "deselect all" would never fire again after selecting all.
    const selectAllActiveRef = useRef(false)

    const handleSelectAll = useCallback(
        async () => {
            // Either the header click itself produced the current selection,
            // or every selectable row happens to already be checked (e.g. the
            // user checked each row by hand) — both read as "everything is
            // selected" and should deselect on this click.
            const isAllSelected =
                selectAllActiveRef.current ||
                (selectedRows.size > 0 && selectedRows.size === selectionTotal)
            if (isAllSelected) {
                selectAllActiveRef.current = false
                setSelectedRows(new Set())
                selectedRowsMapRef.current.clear()
                onSelectionChange?.(new Set())
                lastSelectedIndexRef.current = null
                return
            }
            // Server-paged grid with an all-records callback → fetch all rows first
            if (isServerPaging && onSelectAll) {
                setIsSelectingAll(true)
                try {
                    const allRows = await onSelectAll()
                    const allIds = new Set<unknown>(allRows.map((r) => getRowId(r)))
                    allRows.forEach((r) => selectedRowsMapRef.current.set(getRowId(r), r))
                    setSelectedRows(allIds)
                    onSelectionChange?.(allIds)
                } finally {
                    setIsSelectingAll(false)
                }
            } else {
                const allIds = new Set<unknown>(sortedData.map((r) => getRowId(r)))
                sortedData.forEach((r) => selectedRowsMapRef.current.set(getRowId(r), r))
                setSelectedRows(allIds)
                onSelectionChange?.(allIds)
            }
            selectAllActiveRef.current = true
            lastSelectedIndexRef.current = null
        },
        [
            sortedData,
            onSelectionChange,
            getRowId,
            isServerPaging,
            onSelectAll,
            selectedRows,
            selectionTotal,
        ],
    )

    const handleSelectRow = useCallback(
        (id: unknown, checked: boolean) => {
            // A manual per-row edit means the selection is no longer the exact
            // set "select all" produced — the next header click should select
            // all again rather than assume there's still a full set to clear.
            selectAllActiveRef.current = false
            setSelectedRows((prev) => {
                const next = new Set(prev)
                checked ? next.add(id) : next.delete(id)
                onSelectionChange?.(next)
                return next
            })
        },
        [onSelectionChange],
    )

    const handleRowCheckboxClick = useCallback(
        (e: React.MouseEvent, row: T, rowIndex: number) => {
            const id = getRowId(row)
            if (e.shiftKey && lastSelectedIndexRef.current !== null) {
                selectAllActiveRef.current = false
                const lo = Math.min(lastSelectedIndexRef.current, rowIndex)
                const hi = Math.max(lastSelectedIndexRef.current, rowIndex)
                setSelectedRows((prev) => {
                    const next = new Set(prev)
                    sortedData.slice(lo, hi + 1).forEach((r) => {
                        next.add(getRowId(r))
                        selectedRowsMapRef.current.set(getRowId(r), r)
                    })
                    onSelectionChange?.(next)
                    return next
                })
            } else {
                const checked = !selectedRows.has(id)
                if (checked) {
                    selectedRowsMapRef.current.set(id, row)
                } else {
                    selectedRowsMapRef.current.delete(id)
                }
                handleSelectRow(id, checked)
                lastSelectedIndexRef.current = rowIndex
            }
        },
        [
            getRowId,
            handleSelectRow,
            selectedRows,
            sortedData,
            onSelectionChange,
        ],
    )

    const handleSort = useCallback((field: string) => {
        setSortConfig((prev) => {
            const existing = prev.find((s) => s.field === field)
            if (existing) {
                return existing.direction === 'asc'
                    ? prev.map((s) =>
                          s.field === field
                              ? inheritEntryKey(s, { ...s, direction: 'desc' })
                              : s,
                      )
                    : prev.filter((s) => s.field !== field)
            }
            return [...prev, { field, direction: 'asc' }]
        })
    }, [])

    const addFilter = useCallback(() => {
        const firstColumn = columns.find((col) => col.key !== 'select')
        if (firstColumn) {
            setFilters((prev) => [
                ...prev,
                {
                    field: String(firstColumn.key),
                    operator: 'contains',
                    value: '',
                },
            ])
        }
    }, [columns])

    const removeFilter = useCallback((index: number) => {
        setFilters((prev) => prev.filter((_, i) => i !== index))
    }, [])

    const updateFilter = useCallback(
        (index: number, updates: Partial<GridFilter>) => {
            setFilters((prev) =>
                prev.map((f, i) =>
                    i === index ? inheritEntryKey(f, { ...f, ...updates }) : f,
                ),
            )
        },
        [],
    )

    const exportCSV = useCallback(async () => {
        const cols = orderedColumns.filter(
            (c): c is NonNullable<typeof c> =>
                !!c && String(c.key) !== 'select',
        )
        let rows: T[] = sortedData
        if (onExport) {
            setIsExporting(true)
            try {
                rows = await onExport()
            } catch {
                setIsExporting(false)
                return
            }
            setIsExporting(false)
        }
        const csv = [
            cols.map((c) => c.label).join(','),
            ...rows.map((row) =>
                cols
                    .map((c) => (row as Record<string, unknown>)[String(c.key)])
                    .join(','),
            ),
        ].join('\n')
        downloadCsv(csv, 'platform-grid-export.csv')
    }, [sortedData, orderedColumns, onExport])

    const exportSelectedCSV = useCallback(() => {
        const cols = orderedColumns.filter(
            (c): c is NonNullable<typeof c> => !!c && String(c.key) !== 'select',
        )
        const rows = Array.from(selectedRowsMapRef.current.values())
        const csv = [
            cols.map((c) => c.label).join(','),
            ...rows.map((row) =>
                cols.map((c) => (row as Record<string, unknown>)[String(c.key)]).join(','),
            ),
        ].join('\n')
        downloadCsv(csv, 'export-selected.csv')
    }, [orderedColumns])

    const highlightText = (text: string): React.ReactNode => {
        if (!globalSearch) return text
        const escaped = globalSearch.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`)
        const parts = text.split(new RegExp(`(${escaped})`, 'gi'))
        if (parts.length <= 1) return text
        // Key each fragment by its character offset in the source text
        let offset = 0
        return parts.map((part, i) => {
            const isMatch = i % 2 === 1
            const key = `${isMatch ? 'm' : 't'}${offset}`
            offset += part.length
            return isMatch ? (
                <mark key={key} className="bg-yellow-200 rounded-sm">
                    {part}
                </mark>
            ) : (
                <React.Fragment key={key}>{part}</React.Fragment>
            )
        })
    }

    const updateSortLevel = useCallback(
        (index: number, updates: Partial<GridSort>) => {
            setSortConfig((prev) =>
                prev.map((s, i) =>
                    i === index ? inheritEntryKey(s, { ...s, ...updates }) : s,
                ),
            )
        },
        [],
    )

    const toggleSortLevelDirection = useCallback((index: number) => {
        setSortConfig((prev) =>
            prev.map((s, i) =>
                i === index
                    ? inheritEntryKey(s, {
                          ...s,
                          direction: flipDirection(s.direction),
                      })
                    : s,
            ),
        )
    }, [])

    const removeSortLevel = useCallback((index: number) => {
        setSortConfig((prev) => prev.filter((_, i) => i !== index))
    }, [])

    const toggleGroupCollapsed = useCallback((groupName: string) => {
        setCollapsedGroups((prev) => {
            const next = new Set(prev)
            if (next.has(groupName)) {
                next.delete(groupName)
            } else {
                next.add(groupName)
            }
            return next
        })
    }, [])

    const handleRowClick = (
        e: React.MouseEvent<HTMLTableRowElement>,
        row: T,
        isSelected: boolean,
        globalIndex: number,
    ) => {
        const target = e.target as HTMLElement
        if (target.closest('input, button, a')) return
        if (e.ctrlKey || e.metaKey) {
            handleSelectRow(getRowId(row), !isSelected)
            lastSelectedIndexRef.current = globalIndex
            return
        }
        onRowClick?.(row)
    }

    const handleResizeHandleMouseDown = (
        e: React.MouseEvent,
        colKey: string,
    ) => {
        document.body.classList.add('col-resizing')
        handleResizeStart(e, colKey)
        const cleanup = () => {
            document.body.classList.remove('col-resizing')
            document.removeEventListener('mouseup', cleanup)
        }
        document.addEventListener('mouseup', cleanup)
    }

    const renderSelectAllHeader = () => (
        <div
            className="th-inner"
            style={{
                cursor: 'default',
                padding: '8px',
                justifyContent: 'center',
            }}
        >
            {isSelectingAll ? (
                <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
            ) : (
                <input
                    ref={headerCheckboxRef}
                    id={`${gridId}-checkbox-select-all`}
                    type="checkbox"
                    checked={
                        selectedRows.size > 0 &&
                        selectedRows.size === totalCount
                    }
                    className="w-4 h-4 rounded accent-blue-600 cursor-pointer"
                    onChange={handleSelectAll}
                />
            )}
        </div>
    )

    const renderColumnHeaderContent = (col: GridColumn<T>, colKey: string) => {
        if (enableSorting && col.sortable) {
            const activateSort = () => {
                if (!draggedColumn) handleSort(colKey)
            }
            const sortEntry = sortConfig.find((s) => s.field === colKey)
            return (
                <button
                    type="button"
                    className="th-inner"
                    onClick={activateSort}
                >
                    <span className="th-label">{col.label}</span>
                    <span
                        id={`${gridId}-sort-${colKey}`}
                        className="flex items-center gap-0.5 flex-shrink-0"
                    >
                        <SortIcon direction={sortEntry?.direction} />
                        {sortEntry && sortConfig.length > 1 && (
                            <span className="sort-n-badge">
                                {sortConfig.indexOf(sortEntry) + 1}
                            </span>
                        )}
                    </span>
                </button>
            )
        }
        return (
            <div className="th-inner" style={{ cursor: 'default' }}>
                <span className="th-label">{col.label}</span>
            </div>
        )
    }

    const renderHeaderCell = (col: GridColumn<T>, colIndex: number) => {
        const colKey = String(col.key)
        const isLastCol = colIndex === orderedColumns.length - 1
        return (
            <th
                key={colKey}
                id={`${gridId}-col-${colKey}`}
                style={{
                    width: columnWidths[colKey],
                    minWidth: columnWidths[colKey],
                }}
                className={headerCellClass(
                    col.fixed,
                    draggedColumn === colKey,
                    dragOverColumn === colKey,
                )}
                draggable={!col.fixed && enableColumnReorder}
                onDragStart={(e) => handleDragStart(e, colKey)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, colKey)}
                onDragLeave={() => setDragOverColumn(null)}
                onDrop={(e) => handleDrop(e, colKey)}
            >
                {colKey === 'select'
                    ? renderSelectAllHeader()
                    : renderColumnHeaderContent(col, colKey)}

                {enableColumnResize && colKey !== 'select' && !isLastCol && (
                    // Pointer-only drag affordance; hidden from assistive tech.
                    <div
                        aria-hidden="true"
                        className={`rz${resizingColumn === colKey ? ' active' : ''}`}
                        onMouseDown={(e) =>
                            handleResizeHandleMouseDown(e, colKey)
                        }
                    />
                )}
            </th>
        )
    }

    const renderCellContent = ({
        col,
        columnKey,
        row,
        rowIndex,
        globalIndex,
        isSelected,
        rawValue,
        displayValue,
    }: CellContentArgs<T>): React.ReactNode => {
        if (columnKey === 'select') {
            return enableSelection ? (
                <input
                    id={`${gridId}-checkbox-row-${rowIndex + 1}`}
                    type="checkbox"
                    checked={isSelected}
                    className="w-4 h-4 rounded accent-blue-600 cursor-pointer"
                    onChange={(e) => {
                        e.stopPropagation()
                        handleRowCheckboxClick(
                            e as unknown as React.MouseEvent,
                            row,
                            globalIndex,
                        )
                    }}
                    onClick={(e) => e.stopPropagation()}
                />
            ) : null
        }

        if (customRenderers[columnKey]) {
            return customRenderers[columnKey](rawValue, row)
        }

        if (col.render) {
            return col.render(rawValue, row)
        }

        const builtIn = renderBuiltInCell(col, row, rawValue, displayValue)
        if (builtIn !== undefined) return builtIn

        if (columnKey === 'status' && typeof rawValue === 'string') {
            return <StatusBadge status={rawValue} />
        }

        if (col.type === 'number' && typeof rawValue === 'number') {
            return (
                <span className="font-['JetBrains_Mono',monospace] text-xs">
                    {rawValue.toLocaleString()}
                </span>
            )
        }

        return (
            <CellText value={displayValue}>
                {highlightText(displayValue)}
            </CellText>
        )
    }

    const renderBodyCell = (
        col: GridColumn<T>,
        row: T,
        rowIndex: number,
        globalIndex: number,
        isSelected: boolean,
    ) => {
        const columnKey = String(col.key)
        const rawValue = readField(row, columnKey)
        const displayValue = col.lookup
            ? (col.lookup[rawValue as string | number] ??
              formatDateTimeCell(rawValue))
            : formatDateTimeCell(rawValue)
        const isSelectCol = columnKey === 'select'

        return (
            <td
                key={columnKey}
                className={`text-stone-700 ${
                    isSelectCol ? 'px-2 text-center cursor-pointer' : 'truncate'
                }`}
                style={{
                    width: columnWidths[columnKey],
                    ...(isSelectCol
                        ? {}
                        : {
                              paddingLeft: `${cellPaddingX}px`,
                              paddingRight: `${cellPaddingX}px`,
                          }),
                }}
                onClick={
                    isSelectCol
                        ? (e) => {
                              e.stopPropagation()
                              handleRowCheckboxClick(e, row, globalIndex)
                          }
                        : undefined
                }
            >
                {renderCellContent({
                    col,
                    columnKey,
                    row,
                    rowIndex,
                    globalIndex,
                    isSelected,
                    rawValue,
                    displayValue,
                })}
            </td>
        )
    }

    const renderBodyRow = (row: T, rowIndex: number) => {
        const globalIndex = sortedData.indexOf(row)
        const isSelected = selectedRows.has(getRowId(row))
        return (
            <tr
                key={`${String(getRowId(row))}-${rowIndex}`}
                id={`${gridId}-row-${rowIndex + 1}`}
                className={`transition-colors ${
                    isSelected ? 'pg-row-selected' : 'pg-row-hover'
                } ${onRowClick ? 'cursor-pointer' : ''}`}
                onClick={(e) => handleRowClick(e, row, isSelected, globalIndex)}
            >
                {orderedColumns.map((col) =>
                    renderBodyCell(col, row, rowIndex, globalIndex, isSelected),
                )}
                <td />
            </tr>
        )
    }

    const renderGroupHeader = (groupName: string, rowCount: number) => (
        <tr className="bg-stone-100 hover:bg-stone-150 transition-colors cursor-pointer">
            <td
                colSpan={orderedColumns.length}
                className="px-4 py-2.5"
                onClick={() => toggleGroupCollapsed(groupName)}
            >
                <div className="flex items-center gap-2">
                    {collapsedGroups.has(groupName) ? (
                        <ChevronRight className="w-4 h-4 text-stone-500" />
                    ) : (
                        <ChevronDown className="w-4 h-4 text-stone-500" />
                    )}
                    <span className="text-sm font-semibold text-stone-700">
                        {groupName}
                    </span>
                    <span className="text-xs text-stone-500">
                        ({totalGroupCounts[groupName] ?? rowCount})
                    </span>
                </div>
            </td>
        </tr>
    )

    return (
        <div
            ref={gridRootRef}
            id={gridId}
            className={`pg-root h-full flex flex-col font-['Instrument_Sans',sans-serif] ${className}`}
        >
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Instrument+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

        .table-shadow { box-shadow: 0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.03); }
        .btn-hover { transition: all 0.15s cubic-bezier(0.4,0,0.2,1); }
        .btn-hover:hover { transform: translateY(-1px); }
        .filter-pill { animation: slideIn 0.2s ease-out; }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        /* ── Column header ── */
        thead tr { height: ${rowHeight}px; }
        .column-header {
          height: ${rowHeight}px;
          box-sizing: content-box;
          padding: 0;
          text-align: left;
          font-size: ${fontSize}px;
          font-weight: 500;
          color: ${headerTextColor};
          background: ${headerBg};
          border-bottom: 1px solid #e5e5e3;
          position: relative;
          white-space: nowrap;
          overflow: hidden;
          user-select: none;
        }
        .column-header:not(.fixed) { cursor: grab; }
        .column-header:not(.fixed):active { cursor: grabbing; }

        /* inner div — handles click/hover */
        .th-inner {
          display: flex;
          align-items: center;
          gap: 2px;
          padding: 0 ${cellPaddingX}px;
          height: 100%;
          cursor: pointer;
          transition: background .1s;
        }
        .th-inner:hover { background: ${headerHoverBg}; }
        button.th-inner { width: 100%; border: 0; background: transparent; font: inherit; color: inherit; text-align: left; }
        button.th-inner:hover { background: ${headerHoverBg}; }

        .th-type { font-size: 9px; color: #a8a8a5; opacity: .6; }
        .th-label { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 500; color: ${headerTextColor}; }

        .sort-ico { font-size: 10px; opacity: .28; line-height: 1; }
        .sort-ico.sorted { opacity: 1; color: #2563eb; }
        .sort-n-badge { font-size: 8px; background: #2563eb; color: #fff; border-radius: 3px; padding: 0 3px; line-height: 15px; }

        /* drag states */
        .drag-over { border-left: 2px solid #2563eb !important; background: rgba(37,99,235,0.04) !important; }
        .dragging  { opacity: 0.4; }

        /* resize handle */
        .rz {
          position: absolute;
          right: 0;
          top: 0;
          width: 4px;
          height: 100%;
          cursor: col-resize;
          z-index: 10;
          background: transparent;
          transition: background 0.15s;
        }
        .rz:hover { background: rgba(37,99,235,.4); }
        .rz.active { background: #2563eb; }

        body.col-resizing,
        body.col-resizing * {
          cursor: col-resize !important;
          user-select: none !important;
        }

        /* ── Body rows ── */
        tbody tr { height: ${rowHeight}px; }
        tbody td { vertical-align: middle; font-size: ${fontSize}px; }
        .pg-row-hover:hover { background: ${rowHoverBg}; }
        .pg-row-selected { background: ${rowFocusBg}; }

        /* ── Cell tooltip wrapper ── */
        .cell-tooltip-wrapper.tooltip-wrapper { display: block; width: 100%; }

        /* ── Custom checkbox — consistent in both states ── */
        .pg-root input[type=checkbox] {
          -webkit-appearance: none;
          appearance: none;
          width: 16px;
          height: 16px;
          border: 1.5px solid #cbd5e1;
          border-radius: 3px;
          background: #ffffff;
          cursor: pointer;
          position: relative;
          flex-shrink: 0;
          transition: background 0.1s, border-color 0.1s;
          vertical-align: middle;
        }
        .pg-root input[type=checkbox]:hover { border-color: #94a3b8; }
        .pg-root input[type=checkbox]:checked {
          background: #2563eb;
          border-color: #2563eb;
        }
        .pg-root input[type=checkbox]:checked::after {
          content: '';
          position: absolute;
          left: 50%;
          top: 50%;
          width: 4px;
          height: 8px;
          border: 2px solid #ffffff;
          border-top: none;
          border-left: none;
          transform: translate(-50%, -60%) rotate(45deg);
          border-radius: 0 1px 1px 0;
        }
        .pg-root input[type=checkbox]:indeterminate {
          background: #2563eb;
          border-color: #2563eb;
        }
        .pg-root input[type=checkbox]:indeterminate::after {
          content: '';
          position: absolute;
          left: 50%;
          top: 50%;
          transform: translate(-50%, -50%);
          width: 8px;
          height: 2px;
          background: #ffffff;
          border-radius: 1px;
        }
      `}</style>

            <div className={`flex flex-col flex-1 min-h-0${title || description ? ' p-4' : ''}`}>
                {(title || description) && (
                    <div className="mb-3 shrink-0">
                        {title && (
                            <h1 className="text-base font-semibold text-stone-900">
                                {title}
                            </h1>
                        )}
                        {description && (
                            <p className="text-xs text-stone-500 mt-0.5">
                                {description}
                            </p>
                        )}
                    </div>
                )}

                <div className="bg-white border border-stone-200 overflow-hidden table-shadow flex flex-col flex-1 min-h-0" style={{ borderRadius: `${borderRadius}px` }}>
                    {/* Toolbar */}
                    <div className="flex items-center gap-1.5 px-3 py-2.5 border-b border-stone-200 flex-wrap bg-white shrink-0">
                        {/* Search */}
                        <div className="flex items-center gap-2 border border-stone-200 rounded-md px-2 bg-white hover:border-stone-300 transition-colors w-48 h-8">
                            <Search className="w-3.5 h-3.5 text-stone-400 shrink-0" />
                            <div className="relative flex-1">
                                <input
                                    id={`${gridId}-search`}
                                    type="text"
                                    placeholder="Search..."
                                    value={globalSearch}
                                    className="w-full outline-none text-xs text-stone-700 placeholder:text-stone-400 bg-transparent pr-4"
                                    onChange={(e) => {
                                        setGlobalSearch(e.target.value)
                                        setPage(1)
                                        onSearchChange?.(e.target.value)
                                    }}
                                />
                                {globalSearch && (
                                    <button
                                        id={`${gridId}-btn-search-clear`}
                                        className="absolute right-0 top-1/2 -translate-y-1/2"
                                        onClick={() => {
                                            setGlobalSearch('')
                                            setPage(1)
                                            onSearchChange?.('')
                                        }}
                                    >
                                        <X className="w-3 h-3 text-stone-400 hover:text-stone-600" />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="w-px h-4 bg-stone-200" />

                        {/* Filter */}
                        {enableFilters && (
                            <button
                                id={`${gridId}-btn-filter`}
                                className={toolbarButtonClass(showFilterPanel || filters.length > 0)}
                                onClick={() => {
                                    setShowFilterPanel(!showFilterPanel)
                                    setShowSortPanel(false)
                                    setShowColumnPanel(false)
                                }}
                            >
                                <Filter className="w-3.5 h-3.5" />
                                Filter
                                {filters.length > 0 && (
                                    <span className="bg-blue-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full ml-0.5">
                                        {filters.length}
                                    </span>
                                )}
                            </button>
                        )}

                        {/* Sort */}
                        {enableSorting && (
                            <button
                                id={`${gridId}-btn-sort`}
                                className={toolbarButtonClass(showSortPanel || sortConfig.length > 0)}
                                onClick={() => {
                                    setShowSortPanel(!showSortPanel)
                                    setShowFilterPanel(false)
                                    setShowColumnPanel(false)
                                }}
                            >
                                <MoveVertical className="w-3.5 h-3.5" />
                                Sort
                                {sortConfig.length > 0 && (
                                    <span className="bg-blue-600 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-full ml-0.5">
                                        {sortConfig.length}
                                    </span>
                                )}
                            </button>
                        )}

                        {/* Column search toggle */}
                        <button
                            id={`${gridId}-btn-column-search`}
                            className={toolbarButtonClass(showColumnSearch)}
                            onClick={() =>
                                setShowColumnSearch(!showColumnSearch)
                            }
                        >
                            <SlidersHorizontal className="w-3.5 h-3.5" />
                            Column search
                        </button>

                        {/* Columns */}
                        {enableColumnReorder && (
                            <button
                                id={`${gridId}-btn-columns`}
                                className={toolbarButtonClass(showColumnPanel)}
                                onClick={() => {
                                    setShowColumnPanel(!showColumnPanel)
                                    setShowFilterPanel(false)
                                    setShowSortPanel(false)
                                }}
                            >
                                <Columns className="w-3.5 h-3.5" />
                                Columns
                            </button>
                        )}

                        <div className="ml-auto flex items-center gap-1.5">
                            {enableGrouping && (
                                <select
                                    id={`${gridId}-select-group-by`}
                                    value={groupBy}
                                    className="px-2.5 py-1.5 rounded-md text-xs border border-stone-200 bg-white text-stone-600 hover:border-stone-300 transition-colors cursor-pointer outline-none"
                                    onChange={(e) => {
                                        setGroupBy(e.target.value)
                                        setCollapsedGroups(new Set())
                                    }}
                                >
                                    <option value="">No grouping</option>
                                    {columns
                                        .filter((col) => col.key !== 'select')
                                        .map((col) => (
                                            <option
                                                key={String(col.key)}
                                                value={String(col.key)}
                                            >
                                                Group by {col.label}
                                            </option>
                                        ))}
                                </select>
                            )}
                            {enableExport && (
                                <button
                                    id={`${gridId}-btn-export`}
                                    disabled={isExporting}
                                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-white text-stone-600 border border-stone-200 hover:bg-stone-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                    onClick={exportCSV}
                                >
                                    {isExporting
                                        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                        : <Download className="w-3.5 h-3.5" />
                                    }
                                    {isExporting ? 'Exporting…' : 'Export'}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Quick Filters — auto column pills (section 1) + custom condition pills (section 2) */}
                    {(quickFilterPills.length > 0 ||
                        quickFilters.length > 0) && (
                        <div
                            className="flex items-center gap-1.5 px-3 py-2 flex-wrap shrink-0"
                            style={{ borderBottom: '1px solid #e5e5e3' }}
                        >
                            <span className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mr-0.5">
                                Quick:
                            </span>
                            {quickFilterPills.length > 0 &&
                                ['all', ...quickFilterPills].map((status) => (
                                    <button
                                        key={status}
                                        className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-all ${
                                            statusFilter === status
                                                ? 'bg-stone-900 text-white border-stone-900'
                                                : 'bg-white text-stone-500 border-stone-200 hover:border-stone-400'
                                        }`}
                                        onClick={() =>
                                            setStatusFilter(
                                                statusFilter === status &&
                                                    status !== 'all'
                                                    ? 'all'
                                                    : status,
                                            )
                                        }
                                    >
                                        {status === 'all' ? 'All' : status}
                                    </button>
                                ))}
                            {quickFilterPills.length > 0 &&
                                quickFilters.length > 0 && (
                                    <div className="w-px h-4 bg-stone-300 mx-0.5" />
                                )}
                            {quickFilters.map((qf, i) => (
                                <button
                                    key={qf.label}
                                    className={`px-2.5 py-0.5 rounded-full text-xs font-medium border transition-all ${
                                        activeQuickFilter === i
                                            ? 'bg-stone-900 text-white border-stone-900'
                                            : 'bg-white text-stone-500 border-stone-200 hover:border-stone-400'
                                    }`}
                                    onClick={() =>
                                        setActiveQuickFilter((prev) =>
                                            prev === i ? null : i,
                                        )
                                    }
                                >
                                    {qf.label}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Active filter chips */}
                    {(statusFilter !== 'all' || activeQuickFilter !== null) && (
                        <div
                            className="flex items-center gap-1.5 px-3 py-1.5 flex-wrap"
                            style={{
                                backgroundColor: '#eff6ff',
                                borderBottom: '1px solid #e5e5e3',
                            }}
                        >
                            {statusFilter !== 'all' && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                                    {statusFilter}
                                    <button
                                        className="ml-0.5 hover:text-blue-900"
                                        onClick={() => setStatusFilter('all')}
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            )}
                            {activeQuickFilter !== null &&
                                quickFilters[activeQuickFilter] && (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 border border-orange-200">
                                        {quickFilters[activeQuickFilter].label}
                                        <button
                                            className="ml-0.5 hover:text-orange-900"
                                            onClick={() =>
                                                setActiveQuickFilter(null)
                                            }
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    </span>
                                )}
                        </div>
                    )}

                    {/* Filter Panel */}
                    {showFilterPanel && (
                        <div className="border-b border-stone-200 bg-white p-4 shrink-0">
                            <div className="flex items-center justify-between mb-3">
                                <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider">
                                    Advanced Filters
                                </h3>
                                <div className="flex items-center gap-2">
                                    {['AND', 'OR'].map((logic) => (
                                        <button
                                            key={logic}
                                            id={`${gridId}-btn-filter-logic-${logic.toLowerCase()}`}
                                            className={`px-2 py-1 rounded text-[10px] font-semibold border transition-all ${
                                                filterLogic === logic
                                                    ? 'bg-stone-900 text-white border-stone-900'
                                                    : 'bg-stone-100 text-stone-600 border-stone-200'
                                            }`}
                                            onClick={() =>
                                                setFilterLogic(
                                                    logic as FilterLogic,
                                                )
                                            }
                                        >
                                            {logic}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="space-y-2">
                                {filters.map((filter, index) => (
                                    <div
                                        key={entryKey(filter)}
                                        className="flex items-center gap-2 filter-pill"
                                    >
                                        {index > 0 && (
                                            <span className="text-[10px] font-semibold text-stone-500 bg-stone-100 px-2 py-1 rounded border border-stone-200">
                                                {filterLogic}
                                            </span>
                                        )}
                                        <select
                                            id={`${gridId}-select-filter-field-${index}`}
                                            value={filter.field}
                                            className="px-2 py-1.5 rounded-lg text-xs border border-stone-200 bg-white outline-none cursor-pointer"
                                            onChange={(e) =>
                                                updateFilter(index, {
                                                    field: e.target.value,
                                                })
                                            }
                                        >
                                            {columns
                                                .filter(
                                                    (c) =>
                                                        String(c.key) !==
                                                        'select',
                                                )
                                                .map((col) => (
                                                    <option
                                                        key={String(col.key)}
                                                        value={String(col.key)}
                                                    >
                                                        {col.label}
                                                    </option>
                                                ))}
                                        </select>
                                        <select
                                            id={`${gridId}-select-filter-operator-${index}`}
                                            value={filter.operator}
                                            className="px-2 py-1.5 rounded-lg text-xs border border-stone-200 bg-white outline-none cursor-pointer"
                                            onChange={(e) =>
                                                updateFilter(index, {
                                                    operator: e.target
                                                        .value as GridFilter['operator'],
                                                    value2: undefined,
                                                })
                                            }
                                        >
                                            <option value="contains">
                                                Contains
                                            </option>
                                            <option value="equals">
                                                Equals
                                            </option>
                                            <option value="startsWith">
                                                Starts with
                                            </option>
                                            <option value="endsWith">
                                                Ends with
                                            </option>
                                            <option value="gt">
                                                Greater than
                                            </option>
                                            <option value="lt">
                                                Less than
                                            </option>
                                            <option value="gte">
                                                Greater or equal
                                            </option>
                                            <option value="lte">
                                                Less or equal
                                            </option>
                                            <option value="between">
                                                Between
                                            </option>
                                            <option value="dateRange">
                                                Date range
                                            </option>
                                        </select>
                                        <input
                                            id={`${gridId}-input-filter-value-${index}`}
                                            type={
                                                filter.operator === 'dateRange'
                                                    ? 'date'
                                                    : 'text'
                                            }
                                            value={filter.value}
                                            placeholder={
                                                filter.operator === 'between'
                                                    ? 'From...'
                                                    : 'Value...'
                                            }
                                            className="flex-1 px-2 py-1.5 rounded-lg text-xs border border-stone-200 bg-white outline-none focus:border-blue-400"
                                            onChange={(e) =>
                                                updateFilter(index, {
                                                    value: e.target.value,
                                                })
                                            }
                                        />
                                        {(filter.operator === 'between' ||
                                            filter.operator ===
                                                'dateRange') && (
                                            <input
                                                id={`${gridId}-input-filter-value2-${index}`}
                                                type={
                                                    filter.operator ===
                                                    'dateRange'
                                                        ? 'date'
                                                        : 'text'
                                                }
                                                value={filter.value2 ?? ''}
                                                placeholder="To..."
                                                className="flex-1 px-2 py-1.5 rounded-lg text-xs border border-stone-200 bg-white outline-none focus:border-blue-400"
                                                onChange={(e) =>
                                                    updateFilter(index, {
                                                        value2: e.target.value,
                                                    })
                                                }
                                            />
                                        )}
                                        <button
                                            id={`${gridId}-btn-remove-filter-${index}`}
                                            className="p-1.5 rounded-lg hover:bg-rose-50 text-stone-400 hover:text-rose-600 transition-colors"
                                            onClick={() => removeFilter(index)}
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button
                                id={`${gridId}-btn-add-filter`}
                                className="mt-3 px-3 py-1.5 rounded-lg text-xs font-medium border border-dashed border-stone-300 text-stone-600 hover:bg-stone-50 transition-colors w-full"
                                onClick={addFilter}
                            >
                                + Add Filter
                            </button>
                        </div>
                    )}

                    {/* Sort Panel */}
                    {showSortPanel && (
                        <div className="border-b border-stone-200 bg-white p-4 shrink-0">
                            <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">
                                Sort Order
                            </h3>
                            <div className="space-y-2">
                                {sortConfig.map((sort, index) => (
                                    <div
                                        key={entryKey(sort)}
                                        className="flex items-center gap-2 p-2 rounded-lg bg-stone-50 border border-stone-200"
                                    >
                                        <span className="w-5 h-5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-semibold flex items-center justify-center">
                                            {index + 1}
                                        </span>
                                        <select
                                            value={sort.field}
                                            className="flex-1 px-2 py-1.5 rounded-lg text-xs border border-stone-200 bg-white outline-none cursor-pointer"
                                            onChange={(e) =>
                                                updateSortLevel(index, {
                                                    field: e.target.value,
                                                })
                                            }
                                        >
                                            {columns
                                                .filter((c) => c.sortable)
                                                .map((col) => (
                                                    <option
                                                        key={String(col.key)}
                                                        value={String(col.key)}
                                                    >
                                                        {col.label}
                                                    </option>
                                                ))}
                                        </select>
                                        <button
                                            className={`px-2 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                                                sort.direction === 'asc'
                                                    ? 'bg-blue-50 text-blue-700 border-blue-200'
                                                    : 'bg-white text-stone-600 border-stone-200'
                                            }`}
                                            onClick={() =>
                                                toggleSortLevelDirection(index)
                                            }
                                        >
                                            {sort.direction === 'asc'
                                                ? 'Asc'
                                                : 'Desc'}
                                        </button>
                                        <button
                                            className="p-1.5 rounded-lg hover:bg-rose-50 text-stone-400 hover:text-rose-600 transition-colors"
                                            onClick={() => removeSortLevel(index)}
                                        >
                                            <X className="w-4 h-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button
                                id={`${gridId}-btn-add-sort`}
                                className="mt-3 px-3 py-1.5 rounded-lg text-xs font-medium border border-dashed border-stone-300 text-stone-600 hover:bg-stone-50 transition-colors w-full"
                                onClick={() => {
                                    const available = columns.filter(
                                        (c) =>
                                            c.sortable &&
                                            !sortConfig.some(
                                                (s) =>
                                                    s.field === String(c.key),
                                            ),
                                    )
                                    if (available.length > 0)
                                        setSortConfig((prev) => [
                                            ...prev,
                                            {
                                                field: String(available[0].key),
                                                direction: 'asc',
                                            },
                                        ])
                                }}
                            >
                                + Add Sort Level
                            </button>
                        </div>
                    )}

                    {/* Column Panel */}
                    {showColumnPanel && (
                        <div className="border-b border-stone-200 bg-white p-4 shrink-0">
                            <h3 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">
                                Visible Columns
                            </h3>
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                {columns
                                    .filter((c) => String(c.key) !== 'select')
                                    .map((col) => (
                                        <label
                                            key={String(col.key)}
                                            className={`flex items-center gap-2 px-2 py-1.5 rounded-lg transition-colors ${
                                                col.fixed
                                                    ? 'cursor-not-allowed opacity-60'
                                                    : 'hover:bg-stone-50 cursor-pointer'
                                            }`}
                                        >
                                            <input
                                                id={`${gridId}-checkbox-column-${String(col.key)}`}
                                                type="checkbox"
                                                checked={
                                                    visibleColumns[
                                                        String(col.key)
                                                    ]
                                                }
                                                disabled={col.fixed}
                                                className="w-3.5 h-3.5 rounded accent-blue-600 cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                                                onChange={(e) =>
                                                    setVisibleColumns(
                                                        (prev) => ({
                                                            ...prev,
                                                            [String(col.key)]:
                                                                e.target
                                                                    .checked,
                                                        }),
                                                    )
                                                }
                                            />
                                            <span className="text-xs text-stone-700">
                                                {col.label}
                                            </span>
                                        </label>
                                    ))}
                            </div>
                        </div>
                    )}

                    {/* Selection Actions */}
                    {enableSelection && selectedRows.size > 0 && (
                        <div className="px-4 py-2.5 bg-blue-50 border-b border-blue-200 flex items-center justify-between shrink-0">
                            <span className="text-xs font-medium text-blue-900">
                                {selectedRows.size} row
                                {selectedRows.size === 1 ? '' : 's'} selected
                            </span>
                            <div className="flex items-center gap-2">
                                {enableExport && (
                                    <button
                                        id={`${gridId}-btn-export-selected`}
                                        className="px-3 py-1 rounded-lg text-xs font-medium bg-white text-stone-700 border border-blue-200 hover:bg-blue-100 transition-colors"
                                        onClick={exportSelectedCSV}
                                    >
                                        Export Selected
                                    </button>
                                )}
                                <button
                                    id={`${gridId}-btn-clear-selection`}
                                    className="px-3 py-1 rounded-lg text-xs font-medium bg-white text-rose-700 border border-blue-200 hover:bg-rose-50 transition-colors"
                                    onClick={() => {
                                        setSelectedRows(new Set())
                                        selectedRowsMapRef.current.clear()
                                        onSelectionChange?.(new Set())
                                    }}
                                >
                                    Clear Selection
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Table */}
                    {/* No fixed minHeight here on purpose: an unconditional floor (e.g.
                        "always at least 6 rows tall") can force this wrapper taller than
                        the space actually left once other chrome above it grows — like
                        the selection banner appearing after a row is checked — which
                        pushes the footer below the card's overflow-hidden bounds and
                        hides it. Sizing is left to flex-1/min-h-0 plus the JS clamp
                        above, which always accounts for real available space. */}
                    <div ref={tableWrapRef} className="flex-1 min-h-0 overflow-auto">
                        <table
                            style={{ tableLayout: 'fixed', width: '100%', minWidth: totalColumnWidth }}
                        >
                            <colgroup>
                                {orderedColumns.map((col) => (
                                    <col
                                        key={String(col.key)}
                                        style={{
                                            width: columnWidths[String(col.key)],
                                        }}
                                    />
                                ))}
                                <col />
                            </colgroup>
                            <thead className="sticky top-0 z-10">
                                <tr>
                                    {orderedColumns.map((col, colIndex) => renderHeaderCell(col, colIndex))}
                                    <th style={{ background: '#f5f5f3', borderBottom: '1px solid #e5e5e3' }} />
                                </tr>
                            </thead>

                            {/* Per-column search row */}
                            {showColumnSearch && (
                                <thead>
                                    <tr className="border-b border-stone-200 bg-stone-50/60">
                                        {orderedColumns.map((col) => {
                                            if (!col) return null
                                            const colKey = String(col.key)
                                            return (
                                                <th
                                                    key={colKey}
                                                    className="px-2 py-1.5"
                                                    style={{
                                                        width: columnWidths[colKey],
                                                    }}
                                                >
                                                    {colKey !== 'select' && (
                                                        <input
                                                            id={`${gridId}-col-search-${colKey}`}
                                                            type="text"
                                                            value={
                                                                columnSearchValues[
                                                                    colKey
                                                                ] ?? ''
                                                            }
                                                            placeholder={`${
                                                                col.label ??
                                                                colKey
                                                            }…`}
                                                            className="w-full px-2 py-1 text-xs border border-stone-200 rounded outline-none focus:border-blue-400 bg-white overflow-hidden text-ellipsis"
                                                            onChange={(e) =>
                                                                setColumnSearchValues(
                                                                    (prev) => ({
                                                                        ...prev,
                                                                        [colKey]:
                                                                            e
                                                                                .target
                                                                                .value,
                                                                    }),
                                                                )
                                                            }
                                                        />
                                                    )}
                                                </th>
                                            )
                                        })}
                                        <th className="px-2 py-1.5 bg-stone-50/60 border-b border-stone-200" />
                                    </tr>
                                </thead>
                            )}

                            <tbody className="bg-white divide-y divide-stone-100">
                                {loading && (
                                    <tr>
                                        <td
                                            colSpan={Math.max(orderedColumns.length + 1, 1)}
                                            className="py-12 text-center"
                                        >
                                            <span className="inline-flex items-center gap-2 text-stone-400 text-sm">
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                Loading…
                                            </span>
                                        </td>
                                    </tr>
                                )}
                                {!loading && sortedData.length === 0 && (
                                    <tr>
                                        <td
                                            colSpan={Math.max(orderedColumns.length + 1, 1)}
                                            className="py-12 text-center text-stone-400 text-sm"
                                        >
                                            No records found
                                        </td>
                                    </tr>
                                )}
                                {!loading &&
                                    Object.entries(paginatedData).map(([groupName, rows]) => (
                                        <React.Fragment key={groupName}>
                                            {groupBy && renderGroupHeader(groupName, rows.length)}
                                            {(!groupBy || !collapsedGroups.has(groupName)) &&
                                                rows.map((row, rowIndex) => renderBodyRow(row, rowIndex))}
                                        </React.Fragment>
                                    ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Footer */}
                    <div ref={footerRef} className="px-3 py-2.5 border-t border-stone-200 bg-white flex items-center justify-between gap-3 flex-wrap shrink-0">
                        {/* Left: record count + rows-per-page */}
                        <div className="flex items-center gap-3 text-xs text-stone-500">
                            <span className="font-medium">
                                {pageRangeLabel(currentPage, pageSize, totalCount)}{' '}
                                of {totalCount}
                            </span>
                            <select
                                id={`${gridId}-select-page-size`}
                                value={pageSize}
                                className="px-2 py-1 rounded-md border border-stone-200 bg-white outline-none cursor-pointer text-xs"
                                onChange={(e) => {
                                    const newSize = Number(e.target.value)
                                    if (serverPagination) {
                                        serverPagination.onPageSizeChange?.(newSize)
                                    } else {
                                        setRowsPerPage(newSize)
                                        handlePageChange(1)
                                    }
                                }}
                            >
                                {pageSizeChoices(pageSizeOptions, pageSize, isServerPaging).map((size) => (
                                    <option key={size} value={size}>
                                        {size} / page
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Right: pagination controls */}
                        {totalPages > 1 && (
                            <div className="flex items-center gap-1">
                                {/* First */}
                                <button
                                    id={`${gridId}-btn-nav-first`}
                                    disabled={currentPage === 1}
                                    className="p-1.5 rounded border border-stone-200 bg-white text-stone-500 disabled:opacity-30 hover:bg-stone-50 transition-colors"
                                    onClick={() => handlePageChange(1)}
                                >
                                    <ChevronsLeft className="w-3.5 h-3.5" />
                                </button>
                                {/* Prev */}
                                <button
                                    id={`${gridId}-btn-nav-prev`}
                                    disabled={currentPage === 1}
                                    className="p-1.5 rounded border border-stone-200 bg-white text-stone-500 disabled:opacity-30 hover:bg-stone-50 transition-colors"
                                    onClick={() =>
                                        handlePageChange(
                                            Math.max(1, currentPage - 1),
                                        )
                                    }
                                >
                                    <ChevronLeft className="w-3.5 h-3.5" />
                                </button>

                                {/* Page numbers with ellipsis */}
                                {buildPageList(totalPages, currentPage).map((p) =>
                                    typeof p === 'number' ? (
                                        <button
                                            key={p}
                                            className={`min-w-[28px] h-7 rounded border text-xs font-medium transition-colors ${
                                                currentPage === p
                                                    ? 'bg-blue-600 text-white border-blue-600'
                                                    : 'bg-white text-stone-600 border-stone-200 hover:bg-stone-50'
                                            }`}
                                            onClick={() => handlePageChange(p)}
                                        >
                                            {p}
                                        </button>
                                    ) : (
                                        <span
                                            key={p}
                                            className="px-1 text-xs text-stone-400"
                                        >
                                            …
                                        </span>
                                    ),
                                )}

                                {/* Next */}
                                <button
                                    id={`${gridId}-btn-nav-next`}
                                    disabled={currentPage === totalPages}
                                    className="p-1.5 rounded border border-stone-200 bg-white text-stone-500 disabled:opacity-30 hover:bg-stone-50 transition-colors"
                                    onClick={() =>
                                        handlePageChange(
                                            Math.min(
                                                totalPages,
                                                currentPage + 1,
                                            ),
                                        )
                                    }
                                >
                                    <ChevronRight className="w-3.5 h-3.5" />
                                </button>
                                {/* Last */}
                                <button
                                    id={`${gridId}-btn-nav-last`}
                                    disabled={currentPage === totalPages}
                                    className="p-1.5 rounded border border-stone-200 bg-white text-stone-500 disabled:opacity-30 hover:bg-stone-50 transition-colors"
                                    onClick={() => handlePageChange(totalPages)}
                                >
                                    <ChevronsRight className="w-3.5 h-3.5" />
                                </button>

                                {/* Go to page */}
                                <span className="ml-2 text-xs text-stone-400">
                                    Go to
                                </span>
                                <input
                                    id={`${gridId}-input-goto-page`}
                                    type="number"
                                    min={1}
                                    max={totalPages}
                                    value={goToPage}
                                    placeholder="—"
                                    className="w-12 text-center text-xs border border-stone-200 rounded px-1.5 py-1 outline-none focus:border-blue-400"
                                    onChange={(e) =>
                                        setGoToPage(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            const n = Number.parseInt(goToPage)
                                            if (n >= 1) {
                                                handlePageChange(Math.min(n, totalPages))
                                                setGoToPage('')
                                            }
                                        }
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default PlatformGrid
